/**
 * A still picture of a template's home page — FR-I16, T-4.56, interface I-7.
 *
 * **It is the real demo, not a drawing of one.** `/_demo/<code>` is the site
 * renderer on sample content (FR-I10), so the thumbnail is that page laid out
 * at a real desktop width — 1280px — and scaled down into a 16:10 frame. A
 * thumbnail drawn by different code would stop being true the first time a
 * theme changed, which is the reason the gallery never drew its own preview.
 *
 * Three properties make it a picture rather than an embedded page:
 *
 * * **Lazy.** `loading="lazy"` on the iframe, so a gallery of eight renders the
 *   cards in view and not the rest. Nothing opens on load (FR-I16).
 * * **Non-interactive.** `pointer-events: none`, `tabIndex={-1}` and
 *   `aria-hidden`: the frame never takes a click, a scroll or a Tab stop, and a
 *   screen reader hears the card's name and actions rather than a second copy
 *   of a sample firm's home page.
 * * **Sandboxed with nothing allowed** — an opaque origin and no script, which
 *   is what the public site is anyway (`script-src 'none'`, ADR-0013 §2).
 *
 * A skeleton covers the frame until the iframe says it has loaded, so a slow
 * demo reads as *arriving* rather than as a blank white card.
 *
 * 12B's onboarding imports this (I-7): `TemplateThumb({code, profession})`.
 */

import { Skeleton } from '@fiveox/ui'
import { useEffect, useRef, useState } from 'react'

import { demoHref } from '../lib/api/site'

/** The width the demo is laid out at before it is scaled — a desktop page. */
export const THUMB_LAYOUT_WIDTH = 1280
/** 16:10, so the frame is 800px tall at layout width. */
export const THUMB_LAYOUT_HEIGHT = 800

export interface TemplateThumbProps {
  code: string
  /** The firm's profession, so the sample content is the firm's kind of work
   *  (FR-I10). `null` renders the template's own. */
  profession: string | null
  /** The template's name, for the iframe's `title`. Defaults to the code. */
  name?: string
  className?: string
}

export function TemplateThumb({ code, profession, name, className }: TemplateThumbProps) {
  const box = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(0.25)
  const [loaded, setLoaded] = useState(false)
  // Mounted only once the card is near the viewport. `loading="lazy"` alone
  // is not enough to keep the skeleton honest: a lazy iframe not yet in view
  // fires `load` for its empty document, so a below-the-fold card went white
  // instead of showing that it was still arriving (seen in the 12D shots).
  const [near, setNear] = useState(typeof IntersectionObserver === 'undefined')

  // Scale to the frame's real width. jsdom and very old browsers have no
  // `ResizeObserver`; the default scale is then a reasonable card width.
  useEffect(() => {
    const el = box.current
    if (!el) return
    const measure = () => {
      const width = el.getBoundingClientRect().width
      if (width > 0) setScale(width / THUMB_LAYOUT_WIDTH)
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const el = box.current
    if (!el || near || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setNear(true)
          observer.disconnect()
        }
      },
      { rootMargin: '200px' },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [near])

  return (
    <div
      ref={box}
      data-template-thumb={code}
      className={[
        'relative aspect-[16/10] w-full overflow-hidden bg-muted',
        className ?? '',
      ].join(' ')}
    >
      {near && (
        <iframe
          src={demoHref(code, profession)}
          title={`${name ?? code} — thumbnail`}
          loading="lazy"
          sandbox=""
          tabIndex={-1}
          aria-hidden
          onLoad={() => setLoaded(true)}
          className="pointer-events-none absolute top-0 left-0 origin-top-left border-0 bg-white"
          style={{
            width: THUMB_LAYOUT_WIDTH,
            height: THUMB_LAYOUT_HEIGHT,
            transform: `scale(${scale})`,
          }}
        />
      )}
      {!loaded && (
        <div className="absolute inset-0 space-y-3 bg-background p-4" data-thumb-loading="">
          <Skeleton className="h-3 w-1/3" />
          <Skeleton className="h-8 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
          <div className="grid grid-cols-3 gap-2 pt-2">
            <Skeleton className="h-12" />
            <Skeleton className="h-12" />
            <Skeleton className="h-12" />
          </div>
        </div>
      )}
    </div>
  )
}
